import {
  Avatar,
  DropdownButtonContext,
  DropdownSelect,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { Presence } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useMemo, useState } from 'react';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { useDebounce } from 'react-use';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useCommandHandler } from 'utils/commands';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import { ConfirmTransferModal } from './components/confirmTransferModal/confirmTransferModal';
import { RequestTransferAssistButton } from './components/requestTransferAssistButton/requestTransferAssistButton';
import { TransferToAgentSelectItem } from './components/transferToAgentSelectItem/transferToAgentSelectItem';
import styles from './transferToAgentDropdown.module.scss';
import { comparePresenceStatus } from './utils/comparePresenceStatus';
import { isAgentAvailableForCall } from './utils/isAgentAvailableForCall';
import { sortTransferToAgentItems } from './utils/sortTransferToAgentItems';

export type TransferToAgentItem = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  status: string;
  presence: Presence;
  actions: React.ReactNode;
};

export const TransferToAgentDropdown = (): React.ReactElement => {
  const [transferToUser, setTransferToUser] = useState<TransferToAgentItem>();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>();

  const {
    assignedAgent,
    metaConversation,
    transferToAgent,
    conversationAsyncStatus,
  } = useAppSelector(
    (state) => ({
      assignedAgent:
        state.conversation.metaConversation.conversation.assignment?.agent,
      metaConversation: state.conversation.metaConversation,
      conversationAsyncStatus: state.conversation.asyncStatus.getConversation,
      transferToAgent: state.transferToAgent,
    }),
    shallowEqual,
  );

  const inProgress = useMemo((): boolean => {
    return !!transferToAgent.transferRequests.find(
      (request) =>
        request?.metaConversation?.conversation?.id ===
        metaConversation.conversation.id,
    );
  }, [transferToAgent, metaConversation]);

  const { allPresence: presences } = useAppSelector((state) => ({
    allPresence: state.presence.allPresence,
  }));

  useDebounce(
    () => {
      if (searchQuery !== undefined) {
        setDebouncedSearchQuery(searchQuery);
      }
    },
    150,
    [searchQuery],
  );

  const items: readonly TransferToAgentItem[] = useMemo(() => {
    if (!presences) {
      return [];
    }

    const channel = metaConversation.conversation.initial_channel;

    return presences
      .filter((presence) => presence.presence.status !== 'offline')
      .map((presence) => {
        const canBeCalled = isAgentAvailableForCall(presence.presence);

        const showCallButton =
          channel === 'pstnphone' &&
          canBeCalled &&
          presence.user.id !== assignedAgent?.id;

        return {
          id: presence.user.id,
          name: getUserDisplayName(presence.user),
          status: presence.presence.status,
          avatarUrl: presence.user.avatar_url,
          presence: presence.presence,
          actions: showCallButton && (
            <RequestTransferAssistButton user={presence.user} />
          ),
        };
      })
      .sort(comparePresenceStatus);
  }, [presences, metaConversation, assignedAgent]);

  const filteredItemsBySearchQuery = useMemo(() => {
    if (!debouncedSearchQuery?.length) {
      return items;
    }

    return items.filter((item) =>
      item.name
        .toLowerCase()
        .includes(debouncedSearchQuery.trim().toLowerCase()),
    );
  }, [items, debouncedSearchQuery]);

  const disabledItemsIds: TransferToAgentItem['id'][] = useMemo(() => {
    const channel = metaConversation.conversation.initial_channel;
    let disabledPresences: string[] = [];

    if (channel === 'pstnphone' || channel === 'callback') {
      const disabledPresencesForPhone = filteredItemsBySearchQuery
        .filter((item) => !isAgentAvailableForCall(item.presence))
        .map((item) => item.id);

      disabledPresences = [...disabledPresences, ...disabledPresencesForPhone];
    }

    return assignedAgent
      ? [assignedAgent.id, ...disabledPresences]
      : disabledPresences;
  }, [filteredItemsBySearchQuery, assignedAgent, metaConversation]);

  const filteredAndSortedByDisabledItemsIds = useMemo(() => {
    return sortTransferToAgentItems(
      filteredItemsBySearchQuery,
      disabledItemsIds,
    );
  }, [filteredItemsBySearchQuery, disabledItemsIds]);

  const handleOnToggle = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery(undefined);
      setDebouncedSearchQuery(undefined);
    } else {
      ampli.conversationHeaderTransferToAgentMenuOpened();
    }
  };

  const handleOnAgentSelect = (selectItem: TransferToAgentItem) => {
    ampli.conversationHeaderTransferToAgentItemSelected();
    setTransferToUser(selectItem);
    setSearchQuery(undefined);
    setDebouncedSearchQuery(undefined);
  };

  const emptyMessage = debouncedSearchQuery?.length
    ? 'No search results...'
    : 'No online agents...';

  const dropdownButtonClassName = classNames(
    {
      [styles.dropdownButtonHasAvatar]: !inProgress,
    },
    styles.dropdownButton,
  );

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:transferToAgent">
      Transfer to agent
    </CommandKeybindingsTooltipContent>
  );

  return (
    <>
      <Tooltip
        content={tooltipContent}
        disabled={inProgress}
        wrap="div"
        wrapProps={{
          className: styles.tooltip,
        }}
      >
        <DropdownSelect
          items={filteredAndSortedByDisabledItemsIds}
          itemComponent={TransferToAgentSelectItem}
          selectedItems={assignedAgent?.id ? [assignedAgent?.id] : undefined}
          disabledItems={disabledItemsIds}
          searchPlaceholder="Search for online agents..."
          onSelect={handleOnAgentSelect}
          onToggle={handleOnToggle}
          onSearch={(value) => setSearchQuery(value !== undefined ? value : '')}
          hasSearch={true}
          noArrow={inProgress ? true : false}
          emptyMessage={emptyMessage}
          fixed={true}
          className={styles.dropdownSelect}
          dropdownProps={{
            className: styles.dropdown,
          }}
          buttonProps={{
            variant: 'tertiary',
            disabled: inProgress,
            isLoading: conversationAsyncStatus === 'loading',
            className: dropdownButtonClassName,
            slotStart: inProgress ? undefined : (
              <Avatar user={assignedAgent} size="24" />
            ),
          }}
        >
          {!inProgress && (
            <DropdownButtonContext.Consumer>
              {({ dropdownOpen, openDropdown, closeDropdown }) => (
                <TransferToAgentCommandHandler
                  actionFn={() => {
                    if (!dropdownOpen) {
                      openDropdown();
                    } else {
                      closeDropdown();
                    }
                    handleOnToggle(!dropdownOpen);
                  }}
                />
              )}
            </DropdownButtonContext.Consumer>
          )}
          {inProgress && 'Transfer in progress...'}
          {!inProgress && assignedAgent && `${assignedAgent.name}`}
          {!inProgress && !assignedAgent && 'Unassigned'}
        </DropdownSelect>
      </Tooltip>

      {assignedAgent && transferToUser && metaConversation && (
        <ConfirmTransferModal
          assignedAgent={assignedAgent}
          transferToUser={transferToUser}
          onClose={() => setTransferToUser(undefined)}
          isOpen={!!transferToUser}
          metaConversation={metaConversation}
        />
      )}
    </>
  );
};

const TransferToAgentCommandHandler = ({
  actionFn,
}: {
  actionFn: () => void;
}): null => {
  useCommandHandler({
    name: 'conversation:transferToAgent',
    actionFn: () => {
      actionFn();
    },
  });

  return null;
};
